import { hexToRgb, rgbToHsl, hslToHex } from './colorUtils';
import type { BrandConfig } from './types';

/**
 * Derives an 8-color chart palette from a brand's primary color using hue rotation.
 * Saturation and lightness are varied to create visual distinction.
 */
export function deriveChartPalette(primary: string): string[] {
  const { r, g, b } = hexToRgb(primary);
  const { h, s, l } = rgbToHsl(r, g, b);

  const hueOffsets = [0, 40, 80, 160, 200, 260, 300, 340];
  const lightnessVariants = [0, 0.04, -0.04, 0.08, -0.02, 0.06, -0.06, 0.02];
  const saturationFactors = [1, 0.85, 0.95, 0.8, 1, 0.75, 0.9, 0.85];

  return hueOffsets.map((offset, i) => {
    const newH = (h + offset) % 360;
    const newS = Math.min(1, Math.max(0.3, s * saturationFactors[i]));
    const newL = Math.min(0.65, Math.max(0.35, l + lightnessVariants[i]));
    return hslToHex(newH, newS, newL);
  });
}

/**
 * Returns chart CSS variable overrides if `brand.chartPalette` is set.
 * Otherwise returns `null` (appearances.ts defaults are used).
 */
export function getChartOverrides(brand: BrandConfig): Record<string, string> | null {
  if (!brand.chartPalette || brand.chartPalette.length === 0) return null;

  const overrides: Record<string, string> = {};
  for (let i = 0; i < Math.min(brand.chartPalette.length, 8); i++) {
    overrides[`--ui-chart-${i + 1}`] = brand.chartPalette[i];
  }
  return overrides;
}
